import { Fragment } from 'react';
import type { PropsWithChildren } from 'react';
import Box from '@mui/material/Box';
import { styled } from '@mui/material';
import Typography from '@mui/material/Typography';
import { TextField, Select } from '@calefy-inc/informedMaterial';

/**
 * Shared advisor identity fields for create (dialog) and edit (card) flows.
 * Informed `name` attributes must match `buildAdvisorFormInitialValues` / create mutation.
 * Optional `organizationSelectOptions` adds `name="organization"` as a Select (admin create).
 */

/** Static hints — MUI TextField hides helperText unless invalid; use caption under the field. */
export const AdvisorProfileFieldDescription = styled(Typography)(
  ({ theme }) => ({
    display: 'block',
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(1.5),
    color: theme.palette.text.secondary,
    lineHeight: 1.45,
  }),
);

export type AdvisorProfileFormFieldsLayout = 'dialog' | 'card';

export interface AdvisorProfileFormFieldsProps {
  textfieldClassName: string;
  /** Create vs edit copy for the required name field only. */
  nameFieldDescription: string;
  layout: AdvisorProfileFormFieldsLayout;
  /**
   * When passed, renders an organization `Select` after the name field (all-org picker).
   * Omit on edit cards — organization is fixed server-side for that flow.
   */
  organizationSelectOptions?: ReadonlyArray<{ value: string; label: string }>;
  /** Applied to the organization `Select` when `organizationSelectOptions` is set. */
  organizationSelectClassName?: string;
}

type AdvisorProfileFieldRow = {
  name: string;
  label: string;
  description?: string;
  required?: boolean;
};

const NAME_FIELD_ROW: AdvisorProfileFieldRow = {
  name: 'name',
  label: 'Name',
  required: true,
};

const ADVISOR_PROFILE_FIELD_ROWS: ReadonlyArray<AdvisorProfileFieldRow> = [
  NAME_FIELD_ROW,
  {
    name: 'jurisdiction',
    label: 'Jurisdiction',
    description:
      'Province, territory, or other region where this advisor is authorized to operate. Leave blank if not relevant.',
  },
  {
    name: 'securitiesDealerName',
    label: 'Securities dealer name',
    description:
      'Name of the securities (IIROC) dealer the advisor is registered with, if any. Leave blank if not relevant.',
  },
  {
    name: 'mortgageDealerName',
    label: 'Mortgage dealer name',
    description:
      'Mortgage broker or administrator name when the advisor conducts mortgage business. Leave blank if not relevant.',
  },
  {
    name: 'designationType',
    label: 'Designation type',
    description:
      'Professional or title designation (for example CFP or life licence). Leave blank if not relevant.',
  },
];

export function AdvisorProfileFormFields({
  textfieldClassName,
  nameFieldDescription,
  layout,
  organizationSelectOptions,
  organizationSelectClassName,
}: AdvisorProfileFormFieldsProps) {
  const variant = layout === 'card' ? 'standard' : undefined;
  const fullWidth = layout === 'card' ? true : undefined;

  const rowsAfterName = ADVISOR_PROFILE_FIELD_ROWS.filter(
    (field) => field !== NAME_FIELD_ROW,
  );

  return (
    <>
      <AdvisorProfileFieldRowWrapper key={NAME_FIELD_ROW.name} layout={layout}>
        <TextField
          name={NAME_FIELD_ROW.name}
          label={NAME_FIELD_ROW.label}
          className={textfieldClassName}
          variant={variant}
          fullWidth={fullWidth}
          required={NAME_FIELD_ROW.required === true}
        />
        <AdvisorProfileFieldDescription variant='caption'>
          {nameFieldDescription}
        </AdvisorProfileFieldDescription>
      </AdvisorProfileFieldRowWrapper>
      {organizationSelectOptions != null ? (
        <AdvisorProfileFieldRowWrapper key='organization' layout={layout}>
          <Select
            name='organization'
            label='Organization'
            options={[...organizationSelectOptions]}
            required={true}
            className={organizationSelectClassName}
            fullWidth
          />
          <AdvisorProfileFieldDescription variant='caption'>
            The organization the advisor belongs to.
          </AdvisorProfileFieldDescription>
        </AdvisorProfileFieldRowWrapper>
      ) : null}
      {rowsAfterName.map((field) => (
        <AdvisorProfileFieldRowWrapper key={field.name} layout={layout}>
          <TextField
            name={field.name}
            label={field.label}
            className={textfieldClassName}
            variant={variant}
            fullWidth={fullWidth}
            required={field.required === true}
          />
          <AdvisorProfileFieldDescription variant='caption'>
            {field.description}
          </AdvisorProfileFieldDescription>
        </AdvisorProfileFieldRowWrapper>
      ))}
    </>
  );
}

/** Card layout wraps each field + caption in a Box; dialog keeps a flat Fragment. */
type AdvisorProfileFieldRowWrapperProps = PropsWithChildren<{
  layout: AdvisorProfileFormFieldsLayout;
}>;

function AdvisorProfileFieldRowWrapper({
  layout,
  children,
}: AdvisorProfileFieldRowWrapperProps) {
  if (layout === 'card') {
    return <Box>{children}</Box>;
  }
  return <Fragment>{children}</Fragment>;
}
